import { TextField } from "@mui/material"
import { type FormikValues } from "formik"
import { StrictOmit, type StringKeyOf } from "../../../../../lib/lang"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FormikAutoComplete } from "../../../../../shared/components/forms/Formik"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { type ProductGroupItem } from "../../productGroup/productGroupTypes"
import {
    type FetchProductGroupsResponse,
    fetchProductGroups,
    ProductGroupQuery,
} from "../../productGroup/productGroupApi"

function transformData(data: FetchProductGroupsResponse) {
    return data.productGroups
}

export type ProductGroupOption = StrictOmit<ProductGroupItem, "isActive">

type FormikProductGroupFieldProps<Values extends FormikValues> = Readonly<{
    name: StringKeyOf<Values>
    priceListId?: number
}>

export function FormikProductGroupField<Values extends FormikValues>({
    name,
    priceListId,
}: FormikProductGroupFieldProps<Values>) {
    const { merchantId } = useMerchant()

    const isPriceListSelected = Boolean(priceListId)

    const productGroups = useApiQuery(fetchProductGroups, {
        queryName: ProductGroupQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            priceListId: priceListId,
            isActive: true,
        },
        select: transformData,
        enabled: isPriceListSelected,
    })

    return (
        <LocalizedStrict id="product-create-group-field-label" attrs={{ label: true }}>
            <FormikAutoComplete<Values, ProductGroupOption>
                name={name}
                label="Product group"
                options={productGroups.data ?? []}
                getOptionLabel={(option) => option.name}
                // the value is extended with the group's availability, so match by id
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
                disabled={!isPriceListSelected}
                renderInput={() => <TextField />}
            />
        </LocalizedStrict>
    )
}
